import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Sparkles, CalendarDays, LayoutGrid, Lock, Loader2, Users } from 'lucide-react'
import { PageHeader } from '@/components/layout/page-header'
import { SectionHelp } from '@/components/help/section-help'
import { FieldHint } from '@/components/field-hint'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { useApi } from '@/hooks/use-api'
import { useContentStats } from './use-content'
import { PostsList } from './posts-list'
import { ContentCalendar } from './content-calendar'
import { EngagementList } from './engagement-list'
import { GeneratePostDialog } from './generate-post-dialog'
import type { Post } from './types'

type ViewMode = 'list' | 'calendar' | 'engagement'

// Stat card for the header row
type StatCardProps = {
  readonly label: string
  readonly value: number | string
  readonly highlight?: boolean
  readonly hint?: string
}

function StatCard({ label, value, highlight = false, hint }: StatCardProps) {
  return (
    <div className="rounded-lg border bg-card px-4 py-3">
      <div className="flex items-center gap-1">
        <p className="text-xs text-muted-foreground">{label}</p>
        {hint && <FieldHint label={`What is ${label}?`}>{hint}</FieldHint>}
      </div>
      <p className={cn('mt-0.5 text-2xl font-bold tabular-nums', highlight && 'text-primary')}>
        {value}
      </p>
    </div>
  )
}

function StatCardSkeleton() {
  return (
    <div className="rounded-lg border bg-card px-4 py-3 space-y-1.5">
      <Skeleton className="h-3 w-28" />
      <Skeleton className="h-7 w-12" />
    </div>
  )
}

// Locked state for Starter plan users
function LockedState() {
  const apiFetch = useApi()
  const [loading, setLoading] = useState(false)

  async function handleUpgrade() {
    setLoading(true)
    // 2026-06-07: Agency tier hidden from customers — Content Engine moved
    // to Pro (PLAN_FEATURES.pro.contentEngine = true).
    const res = await apiFetch<{ url: string }>('/api/billing/checkout', {
      method: 'POST',
      body: { plan: 'pro' },
    })
    setLoading(false)
    if (res.success && res.data?.url) {
      window.location.href = res.data.url
    }
  }

  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed py-24 text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
        <Lock className="h-7 w-7 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold">Content Calendar requires the Pro plan</h3>
      <p className="mt-2 max-w-sm text-sm text-muted-foreground">
        Upgrade to Pro to generate AI-powered LinkedIn posts, manage your content calendar, and
        auto-publish with WF5.
      </p>
      <Button className="mt-6 gap-2" onClick={() => void handleUpgrade()} disabled={loading}>
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
        {loading ? 'Redirecting...' : 'Upgrade to Pro'}
      </Button>
    </div>
  )
}

type ContentPageProps = {
  // Plan can be passed in from auth context; defaults to 'pro' for now
  readonly plan?: 'starter' | 'pro' | 'dfy'
}

export function ContentPage({ plan = 'pro' }: ContentPageProps) {
  const [view, setView] = useState<ViewMode>('list')
  const [generateOpen, setGenerateOpen] = useState(false)
  const [_generatedPost, setGeneratedPost] = useState<Post | null>(null)

  const { data: stats, isLoading: statsLoading } = useContentStats()

  const isLocked = plan === 'starter'

  // Deep-link support: callers (e.g. the Quick Actions card on the dashboard)
  // navigate to `/content?new=1` to land directly in the generate-post flow
  // instead of making the user click "Generate Post" again. We strip the
  // param after firing so the dialog doesn't re-open if the user closes it
  // and stays on the page.
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    if (searchParams.get('new') !== '1') return
    if (isLocked) return
    setGenerateOpen(true)
    const next = new URLSearchParams(searchParams)
    next.delete('new')
    setSearchParams(next, { replace: true })
  }, [searchParams, isLocked, setSearchParams])

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Content Calendar"
        subtitle="Plan, create, and publish LinkedIn content."
        actions={
          <div className="flex items-center gap-2">
            <SectionHelp sectionKey="content" />
            {!isLocked && (
              <Button
                onClick={() => setGenerateOpen(true)}
                className="gap-2"
                data-tour="content-generate"
              >
                <Sparkles className="h-4 w-4" />
                Generate Post
              </Button>
            )}
          </div>
        }
      />

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-tour="content-stats">
        {statsLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <StatCard
              label="Total Posts"
              value={stats?.total_posts ?? 0}
              hint="Every post you have created, in any status."
            />
            <StatCard
              label="Published This Week"
              value={stats?.published_this_week ?? 0}
              highlight={(stats?.published_this_week ?? 0) > 0}
              hint="Posts that went live on your feed in the last 7 days."
            />
            <StatCard
              label="Pending Review"
              value={stats?.pending_review ?? 0}
              highlight={(stats?.pending_review ?? 0) > 0}
              hint="AI drafts waiting for you to approve before they publish."
            />
            <StatCard
              label="Upcoming Scheduled"
              value={stats?.upcoming_scheduled ?? 0}
              hint="Approved posts queued to publish automatically later."
            />
          </>
        )}
      </div>

      {isLocked ? (
        <LockedState />
      ) : (
        <>
          {/* View toggle */}
          <div className="flex items-center gap-1 rounded-lg border bg-muted/30 p-1 w-fit" data-tour="content-views">
            <button
              type="button"
              onClick={() => setView('list')}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                view === 'list'
                  ? 'bg-background shadow text-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <LayoutGrid className="h-4 w-4" />
              Posts
            </button>
            <button
              type="button"
              onClick={() => setView('calendar')}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                view === 'calendar'
                  ? 'bg-background shadow text-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <CalendarDays className="h-4 w-4" />
              Calendar
            </button>
            <button
              type="button"
              onClick={() => setView('engagement')}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                view === 'engagement'
                  ? 'bg-background shadow text-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Users className="h-4 w-4" />
              Engagement
            </button>
          </div>

          {/* Main content view */}
          {view === 'list' && <PostsList showGenerateDialog={false} />}
          {view === 'calendar' && <ContentCalendar />}
          {view === 'engagement' && <EngagementList />}
        </>
      )}

      {/* Generate dialog */}
      <GeneratePostDialog
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        onGenerated={(post) => {
          setGeneratedPost(post)
          setGenerateOpen(false)
        }}
      />
    </div>
  )
}
